import { ASSETS, JETTON_ID, SORTING_WALLET } from '@/constants';
import { ASSET, DEPOSIT, WITHDRAWAL } from '@/modules/wallet/types';
import { RootStore } from '@/store';
import { apiRequest, delay } from '@/utils';
import { formatTokenAmount } from '@/utils/formatTokenAmount';
import { storage } from '@/utils/storage';
import { makeAutoObservable } from 'mobx';

export class WalletStore {
  readonly rootStore: RootStore;
  public selectedAssetId: string | null = null;
  public deposits: DEPOSIT[] = [];
  public withdrawals: WITHDRAWAL[] = [];
  public assets: ASSET[] = [];

  private COINGECKO_URL = 'https://api.coingecko.com/api/v3/coins/';
  private COINPAPRIKA_URL = 'https://api.coinpaprika.com/v1/tickers/';
  private CACHE_MINUTES = 5;
  private CACHE_DURATION = this.CACHE_MINUTES * 60 * 1000;

  constructor({ rootStore }: { rootStore: RootStore }) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.rootStore = rootStore;
  }

  get totalBalance() {
    const amount = this.assets.reduce((acc, asset) => {
      acc += asset.balance.inUsd || 0;
      return acc;
    }, 0);
    return {
      amount,
      _amount: formatTokenAmount(amount, 0),
    };
  }

  get realAssets() {
    return this.assets.filter(({ code }) => code !== 'VOTE');
  }

  get mappedAssets() {
    return this.assets.reduce((map: Record<string, ASSET>, asset: ASSET) => {
      map[asset._id] = asset;
      return map;
    }, {});
  }

  get basicAsset() {
    // todo we need to delete this later
    return this.assets.find(({ _id }) => _id === JETTON_ID) || ASSETS['USDT'];
  }

  get selectedAsset() {
    return this.selectedAssetId
      ? this.mappedAssets[this.selectedAssetId]
      : null;
  }

  getAssetById(id: string) {
    return this.mappedAssets[id];
  }

  public setSelectedAsset = (selectedAsset: ASSET) => {
    this.selectedAssetId = selectedAsset._id;
  };

  public withdraw = async (amount: number, jettonId: string) => {
    await apiRequest(
      [
        {
          path: '/v1/withdrawals/request',
          method: 'PUT',
          body: { amount, jettonId },
        },
      ],
      this.rootStore.errorStore,
    );
    await delay(1000);
    await this.getJettonBalances();
  };

  private setAssets = (assets: ASSET[]) => {
    this.assets = assets;
  };

  public mapBalancesOnAssets = (balance: Record<string, any>) => {
    if ('VOTE' in balance) {
      balance.vote = balance.VOTE;
      delete balance.VOTE;
    }

    const promises = this.assets.map(async (asset: ASSET) => {
      const jettonBalance = balance?.[asset._id]?.available ?? 0;
      const nonWithdrawable = balance?.[asset._id]?.nonWithdrawable ?? 0;
      const rate = await this.getTokenRate(asset);
      const available = Number(jettonBalance ?? 0);
      const inUsd = Number((available / 10 ** asset.decimals) * rate);

      return {
        ...asset,
        balance: {
          ...balance[asset._id],
          _withdrawable: Math.max(
            0,
            jettonBalance - nonWithdrawable,
          ).toString(),
          available, // no decimals
          _available: formatTokenAmount(available, asset.decimals), // string, with spaces
          __available: formatTokenAmount(available, asset.decimals, {
            type: 'number',
          }), // number
          _nonWithdrawable: formatTokenAmount(nonWithdrawable, asset.decimals),
          rate,
          inUsd,
          _inUsd: formatTokenAmount(inUsd, 0),
        },
      };
    });

    return Promise.all(promises);
  };

  public getJettonBalances = async () => {
    const [balance] = await apiRequest(
      [
        {
          path: '/v1/account/wallet/balances',
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );

    // update VOTE balance
    if (balance?.vote?.available) {
      this.rootStore.accountStore.updateVoteBalance(balance.vote.available);
    }

    this.setAssets(await this.mapBalancesOnAssets(balance));
  };

  public getJettonsList = async () => {
    const [assets] = await apiRequest(
      [
        {
          path: '/v1/account/jettons',
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    const tokenizedAssets = (Array.isArray(assets) ? assets : []).map(
      (token: ASSET) => ({
        ...(ASSETS[token.name === 'PUSDT' ? 'USDT' : token.name] ||
          ASSETS['USDT']),
        ...token,
      }),
    );

    this.setAssets([
      { ...ASSETS['VOTE'], _id: 'vote' } as ASSET,
      ...tokenizedAssets,
    ]);
    this.setAssets(await this.mapBalancesOnAssets({})); // to fetch rates on non-auth user

    this.selectedAssetId = this.assets?.[0]?._id;
  };

  public getAllTransactions = async () => {
    const [deposits, withdrawals] = await apiRequest(
      [
        {
          path: `/v1/deposits/history?sort=${SORTING_WALLET.DESC}`,
          method: 'GET',
        },
        {
          path: `/v1/withdrawals/history?sort=${SORTING_WALLET.DESC}`,
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    this.deposits = deposits.items;
    this.withdrawals = withdrawals.items;
  };

  public getTokenRate = async (asset: ASSET) => {
    try {
      if (!asset.coingeckoId && !asset.coinpaprikaId) return 0;
      const tokenId = asset.coingeckoId || asset.coinpaprikaId;
      const cachedRateId = `${tokenId}Rate`;
      const cachedTimestampId = `${tokenId}RateTimestamp`;
      const cachedRate = await storage.getItem(cachedRateId);
      const cachedTimestamp = await storage.getItem(cachedTimestampId);
      const now = Date.now();

      if (
        cachedRate &&
        cachedTimestamp &&
        now - parseInt(cachedTimestamp, 10) < this.CACHE_DURATION
      ) {
        return parseFloat(cachedRate);
      }

      let rate;
      if (asset.coingeckoId) {
        const response = await fetch(
          `${this.COINGECKO_URL}${asset.coingeckoId}`,
        );
        const data = await response.json();
        rate = data.market_data.current_price.usd;
      }

      if (asset.coinpaprikaId) {
        const response = await fetch(
          `${this.COINPAPRIKA_URL}${asset.coinpaprikaId}`,
        );
        const data = await response.json();
        rate = data.quotes.USD.price;
      }

      if (!rate) return 0;

      await storage.setItem(cachedRateId, rate.toString());
      await storage.setItem(cachedTimestampId, now.toString());

      return rate;
    } catch (error) {
      console.error(`Error fetching ${asset.coingeckoId} rate:`, error);
      return 0;
    }
  };
}
